import { useState } from 'react';
import {
  formatDate,
  formatPrice,
  formatOrderStatus,
  getOrderStatusColor,
} from '../../utils/helpers';
import { getImageUrl } from '../../services/imageService';
import './OrderHistory.css';

const OrderHistory = ({ orders }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);

  if (!orders || orders.length === 0) {
    return <div className="no-orders">No orders found</div>;
  }

  const toggleOrder = orderId => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  return (
    <div className="order-history">
      <h3>Order History</h3>
      <div className="orders-list">
        {orders.map(order => (
          <div key={order.id} className="order-card">
            <div className="order-header" onClick={() => toggleOrder(order.id)}>
              <div className="order-info">
                <h4>Order #{order.order_number}</h4>
                <p className="order-date">{formatDate(order.created_at)}</p>
              </div>
              <div className="order-status-info">
                <span
                  className="order-status"
                  style={{ color: getOrderStatusColor(order.status) }}
                >
                  {formatOrderStatus(order.status)}
                </span>
                <span className="order-total">{formatPrice(order.total)}</span>
                <span className="order-toggle">
                  {expandedOrder === order.id ? '−' : '+'}
                </span>
              </div>
            </div>

            {expandedOrder === order.id && (
              <div className="order-details">
                <div className="order-section">
                  <h5>Order Items:</h5>
                  <div className="order-items-grid">
                    {order.items?.map(item => {
                      const productImage = item.product?.images?.[0]?.image_url;
                      const imageUrl = productImage
                        ? getImageUrl(productImage)
                        : null;

                      return (
                        <div key={item.id} className="order-item-card">
                          {imageUrl && (
                            <div className="order-item-image">
                              <img
                                src={imageUrl}
                                alt={item.product_name || 'Product'}
                                onError={e => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="order-item-info">
                            <h6 className="order-item-name">
                              {item.product_name}
                            </h6>
                            <div className="order-item-details">
                              <span className="order-item-quantity">
                                Quantity: {item.quantity}
                              </span>
                              <span className="order-item-price">
                                {formatPrice(item.product_price)} each
                              </span>
                            </div>
                            <div className="order-item-subtotal">
                              Subtotal: {formatPrice(item.subtotal)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="order-sections-grid">
                  <div className="order-section">
                    <h5>Shipping Address:</h5>
                    <div className="order-info-block">
                      <p className="order-info-line">
                        <strong>{order.shipping_full_name}</strong>
                      </p>
                      <p className="order-info-line">{order.shipping_address}</p>
                      {order.shipping_city && (
                        <p className="order-info-line">{order.shipping_city}</p>
                      )}
                      <p className="order-info-line">
                        Phone: {order.shipping_phone}
                      </p>
                      <p className="order-info-line">
                        Method: {order.shipping_method?.replace('_', ' ').toUpperCase() || 'Standard'}
                      </p>
                    </div>
                  </div>

                  <div className="order-section">
                    <h5>Payment Information:</h5>
                    <div className="order-info-block">
                      <p className="order-info-line">
                        Method:{' '}
                        <strong>
                          {order.payment_method
                            ?.replace('_', ' ')
                            .replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                        </strong>
                      </p>
                      <p className="order-info-line">
                        Status:{' '}
                        <span
                          style={{
                            color: getOrderStatusColor(order.payment_status),
                            fontWeight: 600,
                          }}
                        >
                          {formatOrderStatus(order.payment_status)}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="order-section">
                  <h5>Summary:</h5>
                  <div className="order-summary-row">
                    <span>Subtotal:</span>
                    <span>{formatPrice(order.subtotal)}</span>
                  </div>
                  <div className="order-summary-row">
                    <span>Shipping:</span>
                    <span>{formatPrice(order.shipping_cost)}</span>
                  </div>
                  <div className="order-summary-row order-total-row">
                    <span>Total:</span>
                    <span>{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderHistory;
